import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { InterestForm } from '@/components/engagement/interest-form';
import { PathwayCards } from '@/components/engagement/pathway-cards';
import {
  getInvolvedPage,
  pathways,
  type EngagementInterest
} from '@/content/engagement';
import { TDC_CONTACT_EMAIL, TDC_CONTACT_MAILTO } from '@/lib/external-links';
import { Button } from '@/components/ui/button';

const hashToInterest: Record<string, EngagementInterest> = {
  give: 'give',
  partner: 'partner',
  advocate: 'advocate',
  register: 'general'
};

function interestFromHash(hash: string): EngagementInterest {
  const key = hash.replace(/^#/, '');
  return hashToInterest[key] ?? 'general';
}

/**
 * `/get-involved` — three engagement pathways + registration form.
 */
export function GetInvolvedPage() {
  const location = useLocation();
  const defaultInterest = interestFromHash(location.hash);

  React.useEffect(() => {
    const previous = document.title;
    document.title = getInvolvedPage.documentTitle;
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <article className="flex flex-col">
      <header className="relative overflow-hidden px-4 pt-12 pb-10 sm:px-6 sm:pt-16 sm:pb-12 lg:px-8">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-cta/12 via-transparent to-primary/10 opacity-90"
        />
        <div className="relative z-10 mx-auto w-full max-w-7xl">
          <p className="text-primary mb-3 text-xs font-medium tracking-[0.2em] uppercase">
            {getInvolvedPage.eyebrow}
          </p>
          <h1
            id="get-involved-heading"
            className="font-display text-foreground max-w-3xl text-4xl font-semibold tracking-tight text-balance sm:text-5xl"
          >
            {getInvolvedPage.title}
          </h1>
          <p className="text-muted-foreground mt-5 max-w-3xl text-base leading-relaxed sm:text-lg">
            {getInvolvedPage.lead}
          </p>
          <div className="mt-8">
            <Button asChild size="lg" variant="cta">
              <Link to="/donate">
                Donate via Tour de Cure
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <PathwayCards
        pathways={[...pathways]}
        headingId="pathway-section-heading"
        heading="Choose a pathway"
      />

      <section
        id="register"
        aria-labelledby="register-heading"
        className="scroll-mt-24 px-4 pb-16 sm:px-6 sm:pb-20 lg:px-8"
      >
        <div className="mx-auto grid w-full max-w-7xl gap-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-start lg:gap-12">
          <div>
            <h2
              id="register-heading"
              className="font-display text-foreground text-2xl font-semibold tracking-tight sm:text-3xl"
            >
              {getInvolvedPage.registerHeading}
            </h2>
            <p className="text-muted-foreground mt-4 text-base leading-relaxed sm:text-lg">
              Tell us how you’d like to help. We’ll prepare a message to the NextOnVera
              inbox at Tour de Cure (
              <a
                className="text-primary underline-offset-4 hover:underline"
                href={TDC_CONTACT_MAILTO}
              >
                {TDC_CONTACT_EMAIL}
              </a>
              ). Individual gifts of any amount can go straight via the Donate button above. No minimum, no gatekeeping.
            </p>
          </div>
          <InterestForm
            key={defaultInterest}
            idPrefix="get-involved"
            defaultInterest={defaultInterest}
            heading="Interest form"
            description="Validated details open a prepared email. Required fields are checked before sending."
          />
        </div>
      </section>
    </article>
  );
}
